import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, upload } from "../api/client";
import { keys, useCharacters } from "../api/queries";
import type { ImportView } from "../api/types";
import { dateTime, text } from "../text";
import { useCurrentUser } from "./AuthPages";

export function CharacterListPage() {
  const characters = useCharacters();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const client = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const onImport = async (file: File) => {
    setImportError(null);
    try {
      const result = await upload<ImportView>("/api/characters/import", file);
      await client.invalidateQueries({ queryKey: keys.characters });
      navigate(`/c/${result.character.id}`, { state: { importMessages: result.messages } });
    } catch (e) {
      setImportError(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <section>
      <header className="page-header">
        <h1>Charaktere</h1>
        <div className="actions">
          <Link to="/neu" className="button primary">
            Neuer Charakter
          </Link>
          <button type="button" className="button secondary" onClick={() => input.current?.click()}>
            Importieren
          </button>
          <input ref={input} type="file" hidden onChange={(e) => e.target.files?.[0] && void onImport(e.target.files[0])} />
        </div>
      </header>
      {importError && <p className="error">{importError}</p>}
      {characters.isLoading && <p className="muted">Lade …</p>}
      {characters.data?.length === 0 && <p className="muted">Noch keine Charaktere. Lege einen neuen an oder importiere eine Datei.</p>}
      <ul className="character-list">
        {characters.data?.map((c) => (
          <li key={c.id}>
            <Link to={`/c/${c.id}`} className="card character-card">
              <strong>{c.name}</strong>
              <span className="muted">{text(c.systemName)}</span>
              <span className={`badge mode-${c.mode}`}>{c.mode === "creation" ? "Erschaffungsmodus" : "Karrieremodus"}</span>
              {user.role === "admin" && <span className="muted">{c.ownerName}</span>}
              <span className="muted small">{dateTime(c.updatedAt)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
